/* OROS PDP sections — behaviour for the hero video, zone map, fit quiz,
   size guide, feature demos and lifestyle video.

   One file, one load. Every module is idempotent (guards on data-oros-init)
   and keys off its own data attribute, so re-running init is harmless. A
   single shopify:section:load handler re-initialises sections added or
   restyled in the theme editor. */
(function () {
  if (window.orosPdpLoaded) return;
  window.orosPdpLoaded = true;

  /* ---------- Hero product video ---------- */
  function initHero(root) {
    if (!root || root.dataset.orosInit === 'true') return;
    root.dataset.orosInit = 'true';

    var video = root.querySelector('[data-oros-video]');
    var playBtn = root.querySelector('[data-oros-play]');
    var muteBtn = root.querySelector('[data-oros-mute]');
    var fill = root.querySelector('[data-oros-progress]');
    if (!video) return;

    function setIcon(btn, name) {
      if (!btn) return;
      btn.querySelectorAll('[data-icon]').forEach(function (icon) {
        icon.hidden = icon.getAttribute('data-icon') !== name;
      });
    }

    if (playBtn) {
      playBtn.addEventListener('click', function () {
        if (video.paused) {
          var p = video.play();
          if (p && typeof p.catch === 'function') p.catch(function () {});
        } else {
          video.pause();
        }
      });
    }

    video.addEventListener('play', function () {
      setIcon(playBtn, 'pause');
      if (playBtn) playBtn.setAttribute('aria-label', 'Pause video');
    });

    video.addEventListener('pause', function () {
      setIcon(playBtn, 'play');
      if (playBtn) playBtn.setAttribute('aria-label', 'Play video');
    });

    if (muteBtn) {
      muteBtn.addEventListener('click', function () {
        video.muted = !video.muted;
        setIcon(muteBtn, video.muted ? 'muted' : 'unmuted');
        muteBtn.setAttribute('aria-label', video.muted ? 'Unmute video' : 'Mute video');
      });
      setIcon(muteBtn, video.muted ? 'muted' : 'unmuted');
    }

    if (fill) {
      video.addEventListener('timeupdate', function () {
        if (!video.duration) return;
        fill.style.width = (video.currentTime / video.duration) * 100 + '%';
      });
    }
  }

  /* ---------- Insulation zone map ---------- */
  function initBodyMap(root) {
    if (!root || root.dataset.orosInit === 'true') return;
    root.dataset.orosInit = 'true';

    var triggers = Array.prototype.slice.call(root.querySelectorAll('[data-oros-zone]'));
    var panels = Array.prototype.slice.call(root.querySelectorAll('[data-oros-zone-panel]'));
    var empty = root.querySelector('[data-oros-zone-empty]');

    function select(id) {
      triggers.forEach(function (t) {
        t.setAttribute('aria-pressed', String(t.getAttribute('data-oros-zone') === id));
      });
      panels.forEach(function (p) {
        p.hidden = p.getAttribute('data-oros-zone-panel') !== id;
      });
      if (empty) empty.hidden = !!id;
    }

    triggers.forEach(function (t) {
      t.addEventListener('click', function () {
        var id = t.getAttribute('data-oros-zone');
        var isActive = t.getAttribute('aria-pressed') === 'true';
        select(isActive ? null : id);
      });
    });

    select(null);
  }

  /* ---------- Fit quiz (3 steps, size matrix, optional variant sync) ---------- */
  var SIZE_ORDER = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];

  var MATRIX = {
    under_64: { under_130: 'XS', '130_155': 'XS', '156_180': 'S', '181_210': 'M', '211_240': 'L', over_240: 'XL' },
    '64_67': { under_130: 'XS', '130_155': 'S', '156_180': 'S', '181_210': 'M', '211_240': 'L', over_240: 'XL' },
    '68_71': { under_130: 'S', '130_155': 'M', '156_180': 'M', '181_210': 'L', '211_240': 'XL', over_240: 'XXL' },
    '72_75': { under_130: 'M', '130_155': 'M', '156_180': 'L', '181_210': 'L', '211_240': 'XL', over_240: 'XXL' },
    over_75: { under_130: 'M', '130_155': 'L', '156_180': 'L', '181_210': 'XL', '211_240': 'XXL', over_240: 'XXL' }
  };

  function baseSize(height, weight) {
    return (MATRIX[height] && MATRIX[height][weight]) || 'M';
  }

  function applyLayering(size, layering) {
    var idx = SIZE_ORDER.indexOf(size);
    var adj = 0;
    if (layering === 'heavy') adj = 1;
    if (layering === 'base_only') adj = -1;
    var next = Math.max(0, Math.min(SIZE_ORDER.length - 1, idx + adj));
    return SIZE_ORDER[next];
  }

  function recommend(answers) {
    return applyLayering(baseSize(answers.height, answers.weight), answers.layering);
  }

  function selectVariantSize(size) {
    // Best-effort: tick a matching size input/option on the product form.
    var normalized = String(size).toLowerCase();
    var radios = document.querySelectorAll('input[type="radio"][name*="ize" i], input[type="radio"][name*="Size" i]');
    for (var i = 0; i < radios.length; i++) {
      if (String(radios[i].value).trim().toLowerCase() === normalized && !radios[i].disabled) {
        radios[i].click();
        return;
      }
    }
    var selects = document.querySelectorAll('select');
    for (var s = 0; s < selects.length; s++) {
      var opts = selects[s].options;
      for (var o = 0; o < opts.length; o++) {
        if (String(opts[o].value).trim().toLowerCase() === normalized) {
          selects[s].value = opts[o].value;
          selects[s].dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }
      }
    }
  }

  function initQuiz(root) {
    if (!root || root.dataset.orosInit === 'true') return;
    root.dataset.orosInit = 'true';

    var steps = Array.prototype.slice.call(root.querySelectorAll('[data-oros-quiz-step]'));
    var bars = Array.prototype.slice.call(root.querySelectorAll('[data-oros-quiz-bar]'));
    var resultEl = root.querySelector('[data-oros-quiz-result]');
    var sizeEl = root.querySelector('[data-oros-quiz-size]');
    var resetBtn = root.querySelector('[data-oros-quiz-reset]');
    var syncVariant = root.getAttribute('data-oros-sync-variant') === 'true';

    var state = { step: 0, answers: {} };

    function render() {
      steps.forEach(function (s, i) {
        s.hidden = i !== state.step || state.step >= steps.length;
      });
      bars.forEach(function (b, i) {
        b.setAttribute('data-state', i < state.step ? 'done' : i === state.step ? 'current' : 'todo');
      });
      if (resultEl) resultEl.hidden = state.step < steps.length;
    }

    steps.forEach(function (stepEl, index) {
      var key = stepEl.getAttribute('data-oros-quiz-step');
      stepEl.querySelectorAll('[data-oros-quiz-option]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          state.answers[key] = btn.getAttribute('data-oros-quiz-option');
          state.step = index + 1;
          if (state.step >= steps.length) {
            var size = recommend(state.answers);
            if (sizeEl) sizeEl.textContent = size;
            if (syncVariant) selectVariantSize(size);
          }
          render();
        });
      });
    });

    if (resetBtn) {
      resetBtn.addEventListener('click', function () {
        state = { step: 0, answers: {} };
        render();
      });
    }

    render();
  }

  /* ---------- Size guide ---------- */
  function initSizeChart(root) {
    if (!root || root.dataset.orosInit === 'true') return;
    root.dataset.orosInit = 'true';

    var tabs = Array.prototype.slice.call(root.querySelectorAll('[data-oros-size-tab]'));
    var rows = Array.prototype.slice.call(root.querySelectorAll('[data-oros-size-row]'));
    var models = Array.prototype.slice.call(root.querySelectorAll('[data-oros-size-model]'));

    function select(size) {
      tabs.forEach(function (t) {
        t.setAttribute('aria-pressed', String(t.getAttribute('data-oros-size-tab') === size));
      });
      rows.forEach(function (r) {
        r.setAttribute('data-active', String(r.getAttribute('data-oros-size-row') === size));
      });
      models.forEach(function (m) {
        m.hidden = m.getAttribute('data-oros-size-model') !== size;
      });
    }

    tabs.forEach(function (t) {
      t.addEventListener('click', function () {
        select(t.getAttribute('data-oros-size-tab'));
      });
    });

    var initial = root.getAttribute('data-oros-default-size');
    if (!initial && tabs.length) initial = tabs[0].getAttribute('data-oros-size-tab');
    if (initial) select(initial);
  }

  /* ---------- Feature demos (expand a card to reveal its clip) ---------- */
  function initDemos(root) {
    if (!root || root.dataset.orosInit === 'true') return;
    root.dataset.orosInit = 'true';

    var toggles = Array.prototype.slice.call(root.querySelectorAll('[data-oros-clip]'));

    toggles.forEach(function (toggle) {
      toggle.addEventListener('click', function () {
        var open = toggle.getAttribute('aria-expanded') === 'true';
        toggles.forEach(function (other) {
          var card = other.closest('.oros-demos__card');
          var media = card ? card.querySelector('[data-oros-clip-media]') : null;
          var video = media ? media.querySelector('video') : null;
          var shouldOpen = other === toggle && !open;
          other.setAttribute('aria-expanded', String(shouldOpen));
          if (media) media.hidden = !shouldOpen;
          if (video && !shouldOpen) video.pause();
        });
      });
    });
  }

  /* ---------- Lifestyle video mute toggle ---------- */
  function initLifestyle(root) {
    if (!root || root.dataset.orosInit === 'true') return;
    root.dataset.orosInit = 'true';

    var video = root.querySelector('[data-oros-life-video]');
    var muteBtn = root.querySelector('[data-oros-life-mute]');
    if (!video || !muteBtn) return;

    function setIcon() {
      muteBtn.querySelectorAll('[data-icon]').forEach(function (icon) {
        icon.hidden = icon.getAttribute('data-icon') !== (video.muted ? 'muted' : 'unmuted');
      });
      muteBtn.setAttribute('aria-label', video.muted ? 'Unmute video' : 'Mute video');
    }

    muteBtn.addEventListener('click', function () {
      video.muted = !video.muted;
      setIcon();
    });
    setIcon();
  }

  /* ---------- Lifestyle carousel (media + text slides) ---------- */
  function initLifeCarousel(root) {
    if (!root || root.dataset.orosInit === 'true') return;
    root.dataset.orosInit = 'true';

    var track = root.querySelector('[data-oros-lifecar-track]');
    var slides = Array.prototype.slice.call(root.querySelectorAll('[data-oros-lifecar-slide]'));
    if (!track || slides.length === 0) return;

    var prev = root.querySelector('[data-oros-lifecar-prev]');
    var next = root.querySelector('[data-oros-lifecar-next]');
    var dotsWrap = root.querySelector('[data-oros-lifecar-dots]');
    var index = 0;
    var dots = [];

    if (dotsWrap) {
      slides.forEach(function (_, i) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'oros-lifecar__dot';
        b.setAttribute('aria-label', 'Go to slide ' + (i + 1));
        b.addEventListener('click', function () { go(i); });
        dotsWrap.appendChild(b);
        dots.push(b);
      });
    }

    function update() {
      if (prev) prev.disabled = index <= 0;
      if (next) next.disabled = index >= slides.length - 1;
      dots.forEach(function (d, i) { d.setAttribute('aria-current', String(i === index)); });
    }

    function go(i) {
      index = Math.max(0, Math.min(slides.length - 1, i));
      track.scrollTo({ left: slides[index].offsetLeft - track.offsetLeft, behavior: 'smooth' });
      update();
    }

    if (prev) prev.addEventListener('click', function () { go(index - 1); });
    if (next) next.addEventListener('click', function () { go(index + 1); });

    var raf;
    track.addEventListener('scroll', function () {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(function () {
        var nearest = 0, best = Infinity;
        slides.forEach(function (s, i) {
          var d = Math.abs((s.offsetLeft - track.offsetLeft) - track.scrollLeft);
          if (d < best) { best = d; nearest = i; }
        });
        index = nearest;
        update();
      });
    }, { passive: true });

    // Per-slide mute toggles (multiple videos, unlike the single lifestyle section).
    root.querySelectorAll('[data-oros-life-mute]').forEach(function (btn) {
      var slide = btn.closest('[data-oros-lifecar-slide]') || root;
      var video = slide.querySelector('[data-oros-life-video]');
      if (!video) return;
      function setIcon() {
        btn.querySelectorAll('[data-icon]').forEach(function (icon) {
          icon.hidden = icon.getAttribute('data-icon') !== (video.muted ? 'muted' : 'unmuted');
        });
        btn.setAttribute('aria-label', video.muted ? 'Unmute video' : 'Mute video');
      }
      btn.addEventListener('click', function () { video.muted = !video.muted; setIcon(); });
      setIcon();
    });

    // Optional auto-advance (ms via data attribute); pauses on hover.
    var autoMs = parseInt(root.getAttribute('data-oros-lifecar-autoplay'), 10) || 0;
    if (autoMs > 0 && slides.length > 1) {
      var timer = setInterval(tick, autoMs);
      function tick() { go(index >= slides.length - 1 ? 0 : index + 1); }
      root.addEventListener('mouseenter', function () { clearInterval(timer); });
      root.addEventListener('mouseleave', function () { timer = setInterval(tick, autoMs); });
    }

    update();
  }

  /* ---------- Bootstrap ---------- */
  var MODULES = [
    ['[data-oros-hero]', initHero],
    ['[data-oros-body-map]', initBodyMap],
    ['[data-oros-quiz]', initQuiz],
    ['[data-oros-size-chart]', initSizeChart],
    ['[data-oros-demos]', initDemos],
    ['[data-oros-lifestyle]', initLifestyle],
    ['[data-oros-life-carousel]', initLifeCarousel]
  ];

  function initAll(scope) {
    var node = scope || document;
    MODULES.forEach(function (m) {
      node.querySelectorAll(m[0]).forEach(m[1]);
    });
  }

  if (document.readyState !== 'loading') initAll();
  else document.addEventListener('DOMContentLoaded', function () { initAll(); });

  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();
